/**
 * Murphy Door Configurator - Implicit Apply Coordinator
 *
 * PURPOSE: Orchestrates the "implicit apply on navigation" UX pattern.
 * When a user clicks a forward category or tab while a BROWSING selection exists,
 * this coordinator ensures:
 * 1. suppressProgressNavigation is set before apply fires (via event subscription)
 * 2. Telemetry events are published for observability
 * 3. Failed implicit applies are gracefully handled
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-auto-navigation-coordinator.js
 *
 * CCP COMPLIANCE:
 * - Event-driven coordination ONLY (no direct module calls)
 * - PPD-First data access via conductor delegation
 * - Publishes ui:* events for navigation coordination
 * - 5-stage event lifecycle for coordination operations (Pattern 3)
 * - Bootstrap barrier pattern with pendingCalls replay (Patterns 5+6)
 * - Performance tracking with configurable thresholds
 * - Comprehensive cleanup for all event subscriptions
 * - Failed event publication on errors (Pattern 14)
 *
 * DATA FLOW:
 * business:implicit_apply_started → coordinateImplicitApply()
 *   → COORDINATION_START → COORDINATION_VALIDATION → COORDINATION_PROGRESS
 *   → ui:implicit_apply_navigate_request → COORDINATION_SUCCESS
 *   (on error) → COORDINATION_FAILED
 *
 * RESPONSIBILITIES:
 * 1. Subscribe to BUSINESS_IMPLICIT_APPLY_STARTED → coordinate with 5-stage lifecycle
 * 2. Subscribe to BUSINESS_IMPLICIT_APPLY_COMPLETED → log completion, track performance
 * 3. Subscribe to BUSINESS_IMPLICIT_APPLY_FAILED → log failure, reset state
 * 4. Bootstrap barrier with pendingCalls replay for race protection
 *
 * @version 2.0.0
 * @author Murphy Door Configurator Team
 */
(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // ============================================================================
  // DEV-ONLY LOGGING HELPER
  // ============================================================================
  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  /**
   * Using global CONSTANTS from md-config-core-constants.js
   * All event names are defined once in the global constants file.
   * NO local EVENT_TYPES objects — CONSTANTS.EVENTS.* exclusively (DRY).
   */

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    IMPLICIT_APPLY_TOTAL: 700, // 700ms max for apply + animation + navigate
    COORDINATION: 50, // 50ms for coordination operation
    EVENT_PROCESSING: 10, // 10ms for event handler processing
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 5: EVENT REPLAY READY (10 points)
  // ============================================================================
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // LOCAL COORDINATION STATE
  // ============================================================================
  /**
   * Tracks the timestamp when an implicit apply started, used for
   * performance measurement of the full apply-animate-navigate cycle.
   */
  let implicitApplyStartTime = null;

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Publishes an event through CoreEventBus with standard metadata.
   * NOTE: Object.freeze() NOT applied here - CoreEventBus.publish() freezes centrally (Pattern 18 DRY).
   *
   * @param {string} eventType - CONSTANTS.EVENTS.* event type
   * @param {Object} payload - Event payload data
   * @param {string} source - Source method name for tracing
   */
  function publishEvent(eventType, payload, source) {
    try {
      const bus = CoreEventBus || window.MurphyDoorConfigurator?.CoreEventBus;
      if (!bus) {
        isDev() &&
          console.warn('[ImplicitApplyCoordinator] CoreEventBus not available');
        return;
      }

      bus.publish(eventType, {
        ...payload,
        module: 'ImplicitApplyCoordinator',
        source: source || 'unknown',
        timestamp: payload.timestamp || Date.now(),
      });
    } catch (error) {
      console.error(
        `[ImplicitApplyCoordinator] Failed to publish ${eventType}:`,
        error
      );
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Tracks operation performance and publishes slow operation events.
   *
   * @param {string} operationType - Operation being tracked
   * @param {number} duration - Duration in milliseconds
   */
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_EVENT_HANDLER_SLOW,
        {
          operation: operationType,
          duration,
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
        },
        'trackPerformance'
      );
    }
  }

  // ============================================================================
  // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
  // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
  // ============================================================================

  /**
   * Coordinates the implicit apply operation with full 5-stage lifecycle.
   * This is the core coordination function that wraps the UI event publication.
   *
   * Lifecycle stages:
   *   1. COORDINATION_START - Operation begins
   *   2. COORDINATION_VALIDATION - Validate eventData has required fields
   *   3. COORDINATION_PROGRESS - Publishing UI coordination event
   *   4. COORDINATION_SUCCESS - Operation completed successfully
   *   5. COORDINATION_FAILED - Operation failed (on error)
   *
   * @param {Object} eventData - Event payload with browsingCategoryId, targetId, targetType
   */
  function coordinateImplicitApply(eventData) {
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COORDINATION_START,
        {
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
          targetType: eventData?.targetType,
        },
        'coordinateImplicitApply'
      );

      // STAGE 2: VALIDATION
      const hasRequiredData =
        eventData && (eventData.browsingCategoryId || eventData.targetId);

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COORDINATION_VALIDATION,
        {
          valid: !!hasRequiredData,
          hasBrowsingCategoryId: !!eventData?.browsingCategoryId,
          hasTargetId: !!eventData?.targetId,
          hasTargetType: !!eventData?.targetType,
        },
        'coordinateImplicitApply'
      );

      if (!hasRequiredData) {
        throw new Error(
          'Missing required data for implicit apply coordination (browsingCategoryId or targetId).'
        );
      }

      // Track start time for performance measurement of full apply cycle
      implicitApplyStartTime = Date.now();

      // STAGE 3: PROGRESS
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COORDINATION_PROGRESS,
        {
          stage: 'publishing_ui_navigate_request',
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
        },
        'coordinateImplicitApply'
      );

      // PATTERN 10: UI Update Events (10 points)
      // Publish UI coordination event for other modules to react
      publishEvent(
        CONSTANTS.EVENTS.UI_IMPLICIT_APPLY_NAVIGATE_REQUEST,
        {
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
          targetType: eventData?.targetType,
        },
        'coordinateImplicitApply'
      );

      // STAGE 4: SUCCESS
      const duration = performance.now() - startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COORDINATION_SUCCESS,
        {
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
          targetType: eventData?.targetType,
          duration,
        },
        'coordinateImplicitApply'
      );

      // PATTERN 12: Performance Tracking
      trackPerformance('COORDINATION', duration);

      isDev() &&
        console.log(
          `[ImplicitApplyCoordinator] Coordination completed in ${duration.toFixed(1)}ms`
        );
    } catch (error) {
      // STAGE 5: FAILED
      const failDuration = performance.now() - startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COORDINATION_FAILED,
        {
          error: error.message,
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
          duration: failDuration,
        },
        'coordinateImplicitApply'
      );

      isDev() &&
        console.error('[ImplicitApplyCoordinator] Coordination failed:', error);

      // Reset state on failure
      implicitApplyStartTime = null;
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // ============================================================================

  /**
   * Handles BUSINESS_IMPLICIT_APPLY_STARTED event.
   * Guards with bootstrap check and delegates to coordinateImplicitApply().
   *
   * @param {Object} eventData - Event payload with browsingCategoryId, targetId, targetType
   */
  function handleImplicitApplyStarted(eventData) {
    // PATTERN 6: Bootstrap Runtime Protection
    if (!bootstrapComplete) {
      isDev() &&
        console.log(
          '[ImplicitApplyCoordinator] Pre-bootstrap call queued for replay'
        );

      // PATTERN 5: Event Replay Ready — queue for replay
      pendingCalls.push({ type: 'started', data: eventData });

      // Publish skipped event for telemetry
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_SKIPPED,
        {
          reason: 'bootstrap_not_complete',
          browsingCategoryId: eventData?.browsingCategoryId,
          targetId: eventData?.targetId,
          queuePosition: pendingCalls.length,
        },
        'handleImplicitApplyStarted'
      );

      return;
    }

    coordinateImplicitApply(eventData);
  }

  /**
   * Handles BUSINESS_IMPLICIT_APPLY_COMPLETED event.
   * Logs completion and tracks performance of the full cycle.
   *
   * @param {Object} eventData - Event payload
   */
  function handleImplicitApplyCompleted(eventData) {
    isDev() &&
      console.log(
        '[ImplicitApplyCoordinator] Implicit apply completed',
        eventData
      );

    // Measure total cycle time if we have a start timestamp
    if (implicitApplyStartTime) {
      const totalDuration = Date.now() - implicitApplyStartTime;
      trackPerformance('IMPLICIT_APPLY_TOTAL', totalDuration);

      isDev() &&
        console.log(
          `[ImplicitApplyCoordinator] Total implicit apply cycle: ${totalDuration}ms`
        );
    }

    // Reset local state
    implicitApplyStartTime = null;
  }

  /**
   * Handles BUSINESS_IMPLICIT_APPLY_FAILED event.
   * Logs the failure and resets state.
   *
   * @param {Object} eventData - Event payload with error details
   */
  function handleImplicitApplyFailed(eventData) {
    isDev() &&
      console.warn(
        '[ImplicitApplyCoordinator] Implicit apply failed',
        eventData
      );

    // Reset local state
    implicitApplyStartTime = null;
  }

  // ============================================================================
  // PATTERN 5 & 6: BOOTSTRAP BARRIER WITH REPLAY (20 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe at module load time for bootstrap event
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;

        isDev() &&
          console.log(
            `[ImplicitApplyCoordinator] Bootstrap complete - replaying ${pendingCalls.length} pending calls`
          );

        // PATTERN 5: Replay queued events
        const callsToReplay = pendingCalls.slice();
        pendingCalls.length = 0;

        callsToReplay.forEach((pending) => {
          if (pending.type === 'started') {
            coordinateImplicitApply(pending.data);
          }
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  /**
   * Initialize the implicit apply coordinator.
   * Sets up all event subscriptions.
   *
   * @param {Object} conductor - The central conductor for delegation
   * @returns {boolean} True if initialization succeeded
   */
  function initialize(conductor) {
    // PATTERN 16: Initialization Guard
    if (isInitialized) {
      isDev() &&
        console.warn(
          '[ImplicitApplyCoordinator] Already initialized, skipping'
        );
      return false;
    }

    if (!conductor) {
      isDev() &&
        console.error(
          '[ImplicitApplyCoordinator] Cannot initialize: conductor is required'
        );
      return false;
    }

    const bus = CoreEventBus || window.MurphyDoorConfigurator?.CoreEventBus;
    if (!bus) {
      isDev() &&
        console.error(
          '[ImplicitApplyCoordinator] Cannot initialize: CoreEventBus not available'
        );
      return false;
    }

    try {
      // PATTERN 4: Event Consumer Handlers
      // Subscribe to implicit apply lifecycle events
      const unsubStarted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_STARTED,
        (eventData) => handleImplicitApplyStarted(eventData)
      );
      cleanupHandlers.push(unsubStarted);

      const unsubCompleted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COMPLETED,
        (eventData) => handleImplicitApplyCompleted(eventData)
      );
      cleanupHandlers.push(unsubCompleted);

      const unsubFailed = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_FAILED,
        (eventData) => handleImplicitApplyFailed(eventData)
      );
      cleanupHandlers.push(unsubFailed);

      // Mark as initialized
      isInitialized = true;

      // Publish module ready event
      publishEvent(
        CONSTANTS.EVENTS.SYSTEM_MODULE_READY,
        {
          module: 'ImplicitApplyCoordinator',
          subscriptionCount: cleanupHandlers.length,
        },
        'initialize'
      );

      isDev() &&
        console.log(
          `[ImplicitApplyCoordinator] Initialized with ${cleanupHandlers.length} event subscriptions`
        );

      return true;
    } catch (error) {
      // PATTERN 14: Failed event on initialization error
      publishEvent(
        CONSTANTS.EVENTS.SYSTEM_MODULE_READY,
        {
          module: 'ImplicitApplyCoordinator',
          success: false,
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
          },
        },
        'initialize'
      );

      isDev() &&
        console.error(
          '[ImplicitApplyCoordinator] Initialization failed:',
          error
        );
      return false;
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset state.
   */
  function cleanup() {
    isDev() && console.log('[ImplicitApplyCoordinator] Cleaning up resources');

    // Unsubscribe all event handlers
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (error) {
        isDev() &&
          console.error(
            '[ImplicitApplyCoordinator] Cleanup handler error:',
            error
          );
      }
    });
    cleanupHandlers.length = 0;

    // Reset state
    isInitialized = false;
    bootstrapComplete = false;
    implicitApplyStartTime = null;
    pendingCalls.length = 0;
  }

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // PUBLIC API (registered on namespace)
  // ============================================================================
  window.MurphyDoorConfigurator.implicitApplyCoordinator = {
    initialize,
    cleanup,

    // Debug/testing access — PATTERN 18: Frozen State Objects
    getState: () =>
      Object.freeze({
        isInitialized,
        bootstrapComplete,
        implicitApplyStartTime,
        subscriptionCount: cleanupHandlers.length,
        pendingCallsCount: pendingCalls.length,
      }),
  };

  isDev() &&
    console.log(
      '[ImplicitApplyCoordinator] Module loaded and registered on namespace'
    );
})(window);
